import { useState, useCallback, useRef, useEffect, useId } from 'react'
import { useMutation } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { Id } from '../../../convex/_generated/dataModel'
import {
  MOTION_PRESETS,
  SPEED_PRESETS,
  matchPreset,
  type KenBurnsConfig,
  type MotionPreset,
} from '@/lib/motionPresets'
import { cn } from '@/lib/utils'
import {
  Layers,
  X,
  Gauge,
} from 'lucide-react'
import { toast } from '@/hooks/use-toast'
import { PillSlider } from '@/components/ui/pill-slider'

function MotionArrow({ preset, instanceId }: { preset: MotionPreset; instanceId: string }) {
  const kb = preset.kenBurns
  const dx = ((kb.endX ?? 0) - (kb.startX ?? 0)) * 200
  const dy = ((kb.endY ?? 0) - (kb.startY ?? 0)) * 200
  const scaleChange = kb.endScale - kb.startScale
  const markerId = `ah-${instanceId}-${preset.id}`

  return (
    <svg viewBox="0 0 32 32" className="w-full h-full" style={{ overflow: 'visible' }} aria-hidden="true">
      <defs>
        <marker id={markerId} markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto">
          <polygon points="0 0, 6 2, 0 4" className="fill-current" />
        </marker>
      </defs>
      {scaleChange > 0.05 && (
        <circle cx="16" cy="16" r="6" className="stroke-current fill-none" strokeWidth="1" strokeDasharray="2 2" opacity={0.4} />
      )}
      {scaleChange < -0.05 && (
        <circle cx="16" cy="16" r="10" className="stroke-current fill-none" strokeWidth="1" strokeDasharray="2 2" opacity={0.4} />
      )}
      {(Math.abs(dx) > 1 || Math.abs(dy) > 1) ? (
        <line
          x1={16 - dx * 0.4}
          y1={16 - dy * 0.4}
          x2={16 + dx * 0.4}
          y2={16 + dy * 0.4}
          className="stroke-current"
          strokeWidth="1.5"
          markerEnd={`url(#${markerId})`}
        />
      ) : scaleChange > 0 ? (
        <>
          <circle cx="16" cy="16" r="3" className="fill-current" opacity={0.3} />
          <circle cx="16" cy="16" r="8" className="stroke-current fill-none" strokeWidth="1.5" />
        </>
      ) : scaleChange < 0 ? (
        <>
          <circle cx="16" cy="16" r="8" className="fill-current" opacity={0.3} />
          <circle cx="16" cy="16" r="3" className="stroke-current fill-none" strokeWidth="1.5" />
        </>
      ) : null}
    </svg>
  )
}


interface MotionPresetPickerProps {
  clipId: string
  kenBurns?: KenBurnsConfig
  speed?: number
  allClipIds?: string[]
  workflowId?: string
}

export function MotionPresetPicker({ clipId, kenBurns, speed, allClipIds, workflowId }: MotionPresetPickerProps) {
  const instanceId = useId()
  const updateEffects = useMutation(api.workflows.updateClipEffects)
  const updateSpeed = useMutation(api.workflows.updateClipSpeed)
  const batchUpdate = useMutation(api.workflows.batchUpdateClipEffects)

  const [showFineTune, setShowFineTune] = useState(false)
  const [localKb, setLocalKb] = useState<KenBurnsConfig | undefined>(kenBurns)
  const saveTimeout = useRef<NodeJS.Timeout>()

  useEffect(() => {
    setLocalKb(kenBurns)
  }, [kenBurns?.startScale, kenBurns?.endScale, kenBurns?.startX, kenBurns?.startY, kenBurns?.endX, kenBurns?.endY])

  useEffect(() => {
    return () => {
      if (saveTimeout.current) clearTimeout(saveTimeout.current)
    }
  }, [])

  const activePreset = kenBurns ? matchPreset(kenBurns) : undefined

  const applyPreset = useCallback(async (preset: MotionPreset | null) => {
    const kb = preset?.kenBurns
    setLocalKb(kb ?? undefined)
    try {
      if (kb) {
        await updateEffects({
          clipId: clipId as Id<'workflowClips'>,
          effects: { kenBurns: kb },
        })
      } else {
        await updateEffects({
          clipId: clipId as Id<'workflowClips'>,
          effects: {},
          clearKenBurns: true,
        })
      }
    } catch (e: any) {
      toast({ title: 'Failed to update motion', description: e.message, variant: 'destructive' })
    }
  }, [clipId, updateEffects])

  const debouncedSave = useCallback((kb: KenBurnsConfig) => {
    if (saveTimeout.current) clearTimeout(saveTimeout.current)
    saveTimeout.current = setTimeout(() => {
      updateEffects({
        clipId: clipId as Id<'workflowClips'>,
        effects: { kenBurns: kb },
      }).catch(() => {})
    }, 300)
  }, [clipId, updateEffects])

  const updateFineTune = useCallback((key: keyof KenBurnsConfig, value: number) => {
    setLocalKb(prev => {
      const next = { startScale: 1, endScale: 1, ...prev, [key]: value }
      debouncedSave(next)
      return next
    })
  }, [debouncedSave])

  const handleSpeed = useCallback(async (s: number) => {
    try {
      await updateSpeed({
        clipId: clipId as Id<'workflowClips'>,
        speed: s,
      })
    } catch (e: any) {
      toast({ title: 'Failed to update speed', description: e.message, variant: 'destructive' })
    }
  }, [clipId, updateSpeed])

  const handleBatchApply = useCallback(async (randomize: boolean) => {
    if (!workflowId || !localKb) return
    try {
      const result = await batchUpdate({
        workflowId: workflowId as Id<'workflows'>,
        kenBurns: localKb,
        randomize,
      })
      toast({ title: `Motion applied to ${result.count} clips` })
    } catch (e: any) {
      toast({ title: 'Failed to batch apply', description: e.message, variant: 'destructive' })
    }
  }, [workflowId, localKb, batchUpdate])

  const categories = [
    { key: 'zoom' as const, label: 'Zoom' },
    { key: 'pan' as const, label: 'Pan' },
    { key: 'combined' as const, label: 'Combined' },
  ]

  return (
    <div className="space-y-3">
      {/* Motion Presets */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
            <Layers size={12} className="text-sky-400" />
            Camera Motion
          </label>
          {kenBurns && (
            <button
              onClick={() => applyPreset(null)}
              className="text-[10px] text-muted-foreground hover:text-destructive flex items-center gap-0.5 transition-colors"
              aria-label="Remove camera motion"
            >
              <X size={10} />
              Remove
            </button>
          )}
        </div>

        {categories.map(cat => {
          const presets = MOTION_PRESETS.filter(p => p.category === cat.key)
          return (
            <div key={cat.key} className="mb-2">
              <span className="text-[10px] text-muted-foreground/60 uppercase tracking-wider">{cat.label}</span>
              <div className="grid grid-cols-3 gap-1 mt-1">
                {presets.map(preset => {
                  const isActive = activePreset?.id === preset.id
                  return (
                    <button
                      key={preset.id}
                      onClick={() => applyPreset(isActive ? null : preset)}
                      title={preset.description}
                      aria-label={`${preset.name}: ${preset.description}${isActive ? ' (active)' : ''}`}
                      aria-pressed={isActive}
                      className={cn(
                        'group relative flex flex-col items-center gap-0.5 rounded-md border p-1.5 transition-all text-center',
                        isActive
                          ? 'border-sky-500 bg-sky-500/10 text-sky-400'
                          : 'border-border hover:border-foreground/20 hover:bg-surface-2 text-muted-foreground hover:text-foreground'
                      )}
                    >
                      <div className="w-6 h-6 relative">
                        <div className={cn(
                          'transition-transform duration-300',
                          !isActive && 'group-hover:scale-110'
                        )}>
                          <MotionArrow preset={preset} instanceId={instanceId} />
                        </div>
                      </div>
                      <span className="text-[9px] leading-tight">{preset.name}</span>
                    </button>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>

      {/* Fine-tune controls */}
      {localKb && (
        <div>
          <button
            onClick={() => setShowFineTune(!showFineTune)}
            className="text-[10px] text-muted-foreground hover:text-foreground transition-colors"
          >
            {showFineTune ? 'Hide' : 'Show'} fine-tune controls
          </button>

          {showFineTune && (
            <div className="mt-2 space-y-1 p-1 rounded-lg">
              <PillSlider label="Start zoom" value={localKb.startScale} min={0.5} max={2} step={0.05} onChange={v => updateFineTune('startScale', v)} displayMultiplier={1} displayPrecision={2} unit="" ticks={6} />
              <PillSlider label="End zoom" value={localKb.endScale} min={0.5} max={2} step={0.05} onChange={v => updateFineTune('endScale', v)} displayMultiplier={1} displayPrecision={2} unit="" ticks={6} />
              <PillSlider label="Start X" value={localKb.startX ?? 0} min={-0.15} max={0.15} step={0.01} onChange={v => updateFineTune('startX', v)} displayMultiplier={1} displayPrecision={2} unit="" ticks={6} />
              <PillSlider label="End X" value={localKb.endX ?? 0} min={-0.15} max={0.15} step={0.01} onChange={v => updateFineTune('endX', v)} displayMultiplier={1} displayPrecision={2} unit="" ticks={6} />
              <PillSlider label="Start Y" value={localKb.startY ?? 0} min={-0.15} max={0.15} step={0.01} onChange={v => updateFineTune('startY', v)} displayMultiplier={1} displayPrecision={2} unit="" ticks={6} />
              <PillSlider label="End Y" value={localKb.endY ?? 0} min={-0.15} max={0.15} step={0.01} onChange={v => updateFineTune('endY', v)} displayMultiplier={1} displayPrecision={2} unit="" ticks={6} />
            </div>
          )}
        </div>
      )}

      {/* Batch apply */}
      {localKb && workflowId && (allClipIds?.length ?? 0) > 1 && (
        <div className="flex gap-1.5">
          <button
            onClick={() => handleBatchApply(false)}
            className="flex-1 text-[10px] px-2 py-1.5 rounded-md bg-muted/80 hover:bg-muted text-muted-foreground hover:text-secondary-foreground transition-colors font-medium"
          >
            Apply to all clips
          </button>
          <button
            onClick={() => handleBatchApply(true)}
            className="flex-1 text-[10px] px-2 py-1.5 rounded-md bg-muted/80 hover:bg-muted text-muted-foreground hover:text-secondary-foreground transition-colors font-medium"
          >
            Apply with variety
          </button>
        </div>
      )}

      {/* Speed Controls */}
      <div>
        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5 mb-2">
          <Gauge size={12} className="text-amber-400" />
          Speed
        </label>
        <div className="flex rounded-lg bg-muted/80 p-0.5 gap-0.5">
          {SPEED_PRESETS.map(s => (
            <button
              key={s}
              onClick={() => handleSpeed(s)}
              aria-label={`${s}x speed`}
              aria-pressed={(speed ?? 1) === s}
              className={cn(
                'flex-1 text-[11px] py-1.5 rounded-md transition-all font-medium',
                (speed ?? 1) === s
                  ? 'bg-background shadow-sm text-foreground'
                  : 'text-muted-foreground hover:text-secondary-foreground'
              )}
            >
              {s}x
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
